import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { GitMerge, AlertTriangle, Check, X, ShieldAlert } from "lucide-react";

const show = (v) => (v === null || v === undefined || v === "" ? "—" : typeof v === "boolean" ? (v ? "Sì" : "No") : String(v));
const vuoto = (v) => v === null || v === undefined || v === "";

function Scegli({ group, keepId, setKeepId, dropId, setDropId }) {
  const keep = group.schede.find((s) => s.id === keepId);
  const drops = group.schede.filter((s) => s.id !== keepId && s.tipo === keep?.tipo);
  return (
    <>
      <p className="text-xs text-[var(--muted)] mb-2">1. Scegli la scheda da <b>mantenere</b>:</p>
      <div className="space-y-1.5 mb-3">
        {group.schede.map((s) => (
          <label key={s.id} data-testid={`merge-keep-${s.id}`} className={`flex items-center gap-2 p-2.5 rounded-xl border text-sm cursor-pointer ${keepId === s.id ? "border-[#FC82ED] bg-[#FC82ED]/5" : "border-[var(--border)]"}`}>
            <input type="radio" name="keep" checked={keepId === s.id} onChange={() => { setKeepId(s.id); setDropId(group.schede.find((x) => x.id !== s.id && x.tipo === s.tipo)?.id || null); }} />
            <span className="font-medium">{s.nome}</span>
            <span className="ml-auto text-[10px] uppercase font-bold text-[var(--muted)]">{s.tipo}</span>
          </label>
        ))}
      </div>
      <p className="text-xs text-[var(--muted)] mb-2">2. Scegli il doppione da unire (stesso tipo):</p>
      {drops.length === 0 ? <p data-testid="merge-no-drop" className="text-xs text-amber-600 mb-3">Nessun doppione dello stesso tipo: schede di tipo diverso restano separate.</p> : (
        <div className="space-y-1.5 mb-3">
          {drops.map((s) => (
            <label key={s.id} data-testid={`merge-drop-${s.id}`} className={`flex items-center gap-2 p-2.5 rounded-xl border text-sm cursor-pointer ${dropId === s.id ? "border-slate-800 bg-slate-50" : "border-[var(--border)]"}`}>
              <input type="radio" name="drop" checked={dropId === s.id} onChange={() => setDropId(s.id)} />
              <span className="font-medium">{s.nome}</span>
            </label>
          ))}
        </div>
      )}
    </>
  );
}

function Confronto({ ant, scelte, setScelte }) {
  const diversi = ant.campi.filter((c) => !c.uguale);
  const uguali = ant.campi.length - diversi.length;
  return (
    <>
      {ant.consenso_negato && (
        <div data-testid="merge-privacy-warning" className="mb-3 p-3 rounded-xl border border-red-200 bg-red-50 text-red-700 text-xs flex items-start gap-2">
          <ShieldAlert size={15} className="shrink-0 mt-0.5" /> <span><b>Attenzione:</b> una delle due schede ha il consenso privacy NO. Il consenso negato verrà mantenuto.</span>
        </div>
      )}
      <p className="text-xs text-[var(--muted)] mb-2">Tocca il valore da tenere per ogni campo. Il valore scelto è evidenziato.</p>
      <div className="grid grid-cols-[1fr_1fr_1fr] gap-1.5 text-[11px] font-bold uppercase text-[var(--muted)] mb-1 px-1">
        <span>Campo</span><span className="truncate">Resta: {ant.keep.nome}</span><span className="truncate">Doppione: {ant.drop.nome}</span>
      </div>
      <div className="space-y-1.5 max-h-[45vh] overflow-y-auto pr-1" data-testid="merge-compare-table">
        {diversi.length === 0 && <p className="text-sm text-[var(--muted)] p-2">Nessun campo diverso tra le due schede.</p>}
        {diversi.map((c) => (
          <div key={c.campo} data-testid={`merge-row-${c.campo}`} className="grid grid-cols-[1fr_1fr_1fr] gap-1.5 items-stretch">
            <span className="text-xs font-semibold self-center">{c.label}</span>
            {["keep", "drop"].map((lato) => {
              const sel = scelte[c.campo] === lato;
              const disabled = vuoto(c[lato]);
              return (
                <button key={lato} type="button" disabled={disabled} data-testid={`merge-pick-${c.campo}-${lato}`}
                  onClick={() => setScelte({ ...scelte, [c.campo]: lato })}
                  className={`text-left text-xs p-2 rounded-lg border break-words transition-colors ${sel ? "border-[#FC82ED] bg-[#FC82ED]/15 font-semibold" : "border-[var(--border)] bg-white"} ${disabled ? "opacity-40 cursor-not-allowed" : "hover:border-[#FC82ED]/60"}`}>
                  {sel && <Check size={12} className="inline mr-1 text-[#b84fae]" />}{show(c[lato])}
                </button>
              );
            })}
          </div>
        ))}
      </div>
      {uguali > 0 && <p className="text-[11px] text-[var(--muted)] mt-2">{uguali} campi identici non mostrati.</p>}
      <div className="mt-3 p-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-[11px] text-slate-600" data-testid="merge-sposta">
        Passeranno alla scheda che resta: {ant.sposta.appuntamenti} appuntamenti · {ant.sposta.documenti} documenti · {ant.sposta.comunicazioni} comunicazioni · {ant.sposta.storico} voci di storico.
      </div>
    </>
  );
}

export default function MergeCompareModal({ group, onClose, onDone }) {
  const first = group.schede[0];
  const [keepId, setKeepId] = useState(first?.id);
  const [dropId, setDropId] = useState(group.schede.find((s) => s.id !== first?.id && s.tipo === first?.tipo)?.id || null);
  const [step, setStep] = useState("scegli");
  const [ant, setAnt] = useState(null);
  const [scelte, setScelte] = useState({});
  const [busy, setBusy] = useState(false);
  const tipo = group.schede.find((s) => s.id === keepId)?.tipo;

  const confronta = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/clienti/unisci/anteprima?tipo=${tipo}&keep_id=${keepId}&drop_id=${dropId}`);
      const sc = {};
      r.data.campi.forEach((c) => { sc[c.campo] = vuoto(c.keep) ? "drop" : "keep"; });
      setAnt(r.data); setScelte(sc); setStep("confronta");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore nel confronto"); } finally { setBusy(false); }
  };

  const unisci = async () => {
    setBusy(true);
    const campi = {};
    ant.campi.forEach((c) => { if (scelte[c.campo] === "drop" && !c.uguale) campi[c.campo] = c.drop; });
    try {
      await api.post("/clienti/unisci", { tipo, keep_id: keepId, drop_id: dropId, campi });
      toast.success("Contatti uniti");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore unione"); } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-3" data-testid="merge-modal">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl border border-[var(--border)] w-full max-w-2xl p-5 shadow-xl max-h-[94vh] overflow-y-auto">
        <div className="flex items-center gap-2 mb-1 font-display font-extrabold text-lg">
          <GitMerge size={18} className="text-[#FC82ED]" /> Unisci contatti doppi
          <button onClick={onClose} data-testid="merge-close" className="ml-auto p-1.5 rounded-lg hover:bg-slate-100"><X size={18} /></button>
        </div>
        <p className="text-xs text-[var(--muted)] mb-3">Telefono: {group.telefono}. Nessun dato andrà perso: appuntamenti, documenti e storico passano alla scheda che resta.</p>

        {step === "scegli" && <Scegli group={group} keepId={keepId} setKeepId={setKeepId} dropId={dropId} setDropId={setDropId} />}
        {step === "confronta" && ant && <Confronto ant={ant} scelte={scelte} setScelte={setScelte} />}
        {step === "conferma" && ant && (
          <div data-testid="merge-confirm-box" className="p-4 rounded-xl border-2 border-[#FC82ED]/50 bg-[#FC82ED]/5 text-sm">
            <div className="flex items-center gap-2 font-semibold mb-1"><AlertTriangle size={16} className="text-[#b84fae]" /> Confermi l'unione?</div>
            <p className="text-xs text-slate-600">"{ant.drop.nome}" verrà unito in "{ant.keep.nome}" e archiviato.{ant.consenso_negato ? " Il consenso negato verrà mantenuto." : ""}</p>
          </div>
        )}

        <div className="flex gap-2 mt-4">
          <button onClick={step === "scegli" ? onClose : () => setStep(step === "conferma" ? "confronta" : "scegli")} data-testid="merge-back-btn"
            className="flex-1 py-2.5 rounded-xl bg-[#F7F7F8] border border-[var(--border)] text-sm font-medium">{step === "scegli" ? "Annulla" : "Indietro"}</button>
          {step === "scegli" && <button data-testid="merge-compare-btn" onClick={confronta} disabled={busy || !dropId} className="flex-1 py-2.5 rounded-xl bg-[#111111] text-white text-sm font-semibold disabled:opacity-50">{busy ? "…" : "Confronta campi"}</button>}
          {step === "confronta" && <button data-testid="merge-next-btn" onClick={() => setStep("conferma")} className="flex-1 py-2.5 rounded-xl bg-[#111111] text-white text-sm font-semibold">Unisci</button>}
          {step === "conferma" && <button data-testid="merge-confirm-btn" onClick={unisci} disabled={busy} className="flex-1 py-2.5 rounded-xl bg-[#FC82ED] text-black text-sm font-semibold disabled:opacity-50">{busy ? "Unisco…" : "Unisci"}</button>}
        </div>
      </div>
    </div>
  );
}
